import React, { useState, useEffect } from 'react';
import TaskModal from './TaskModal';
import TaskViewModal from './TaskViewModal'; // YENİ: Görüntüleme modalı
import { PlusIcon, MagnifyingGlassIcon, ChatBubbleOvalLeftEllipsisIcon, PencilSquareIcon, EyeIcon } from '@heroicons/react/24/outline'; // YENİ: EyeIcon eklendi
import { db } from '../../config/firebaseConfig';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

const STATUS_COLORS = {
  'Done': { bg: '#00c875', text: '#fff' },
  'Working on it': { bg: '#fdab3d', text: '#fff' },
  'Stuck': { bg: '#e2445c', text: '#fff' },
  'Waiting For Info': { bg: '#0086c0', text: '#fff' },
  'Not Started Yet': { bg: '#c4c4c4', text: '#fff' }
};

const getProgressValue = (status) => {
  switch(status) {
    case 'Done': return 100;
    case 'Working on it': return 50;
    case 'Waiting For Info': return 25;
    case 'Stuck': return 10;
    default: return 0;
  }
};

const TaskBoard = () => {
  // Düzenleme/Ekleme Modalı State'leri
  const [showModal, setShowModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // YENİ: Görüntüleme Modalı State'leri
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);

  // Veri State'leri
  const [tasks, setTasks] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtreleme ve Sayfalama State'leri
  const [activeTab, setActiveTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Önce görevleri çek (Eğer kurallarımız doğruysa, hasAccess('manage-tasks') burayı yetkilendirecek)
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        setTasks(tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // 2. Takım üyelerini çekmek için ayrı bir mantık kullanıyoruz.
        // Hata almamak için sadece adminlerin veya yetkisi olanların çekmesini sağlamalıyız.
        // Veya daha iyisi: Team üyelerini çekmek yerine sadece task'lerdeki bilgileri kullan.
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const staffList = usersSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          // Sadece rolü olanları filtrele
          .filter(u => ['admin', 'editor', 'marketing', 'order'].includes(u.role));
        setTeamUsers(staffList);
      } catch (error) {
        console.error("Error fetching data (Check Firestore Rules): ", error);
        // Hata durumunda boş liste ile devam et
      } finally {
        setLoading(false);
      }
    };
    fetchData();
}, []);

  const refreshTasks = async () => {
    const tasksSnapshot = await getDocs(collection(db, 'tasks'));
    setTasks(tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // Düzenleme Modalı İşlemleri
  const handleOpenModal = (task = null) => {
    setSelectedTask(task);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    setShowModal(false);
  };

  // YENİ: Görüntüleme Modalı İşlemleri
  const handleOpenViewModal = (task) => {
    setViewingTask(task);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setViewingTask(null);
    setShowViewModal(false);
  };

  // Firebase Kayıt/Silme
  const handleSaveTask = async (taskData) => {
    if (selectedTask) {
      await updateDoc(doc(db, 'tasks', selectedTask.id), taskData);
    } else {
      await addDoc(collection(db, 'tasks'), { ...taskData, createdAt: serverTimestamp() });
    }
    refreshTasks();
    handleCloseModal();
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      await deleteDoc(doc(db, 'tasks', taskId));
      refreshTasks();
      handleCloseModal();
    }
  };

  // NATIVE SELECT İÇİN DURUM GÜNCELLEME
  const handleStatusChange = async (taskId, newStatus) => {
    setTasks(prevTasks => prevTasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    try {
      await updateDoc(doc(db, 'tasks', taskId), { status: newStatus });
    } catch (error) {
      console.error("Error updating status: ", error);
      refreshTasks(); 
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Filtreleme
  const filteredTasks = tasks.filter(task => {
    const matchTab = activeTab === 'All' || task.assigneeId === activeTab;
    const matchSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'All' || task.status === statusFilter;
    return matchTab && matchSearch && matchStatus;
  });

  // Sayfalama
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTasks = filteredTasks.slice(startIndex, startIndex + itemsPerPage);

  // Gruplama
  const groupedTasks = paginatedTasks.reduce((acc, task) => {
    const groupName = task.group || 'Main table';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(task);
    return acc;
  }, {});

  useEffect(() => { setCurrentPage(1); }, [activeTab, searchTerm, statusFilter]);

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      
      {/* TABS (APP STYLE) */}
      <div className="d-flex align-items-center mb-4 gap-2" style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '10px', scrollbarWidth: 'none' }}>
        <button 
          className="btn btn-sm px-4 fw-medium border-0 shadow-sm" 
          style={{ borderRadius: '20px', backgroundColor: activeTab === 'All' ? '#2d3436' : '#fff', color: activeTab === 'All' ? '#fff' : '#636e72', transition: '0.2s' }} 
          onClick={() => setActiveTab('All')}>
          All Tasks
        </button>
        {teamUsers.map(u => (
          <button 
            key={u.id}
            className="btn btn-sm px-4 fw-medium border-0 shadow-sm" 
            style={{ borderRadius: '20px', backgroundColor: activeTab === u.id ? '#6c5ce7' : '#fff', color: activeTab === u.id ? '#fff' : '#636e72', transition: '0.2s' }} 
            onClick={() => setActiveTab(u.id)}>
            {u.displayName?.split(' ')[0] || u.email.split('@')[0]}
          </button>
        ))}
      </div>

      {/* TOOLBAR */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-3 bg-white p-3 rounded-4 shadow-sm border-0">
        <button className="btn btn-sm d-flex align-items-center gap-1 shadow-sm px-3" style={{ backgroundColor: '#0073ea', color: 'white', borderRadius: '20px', border: 'none' }} onClick={() => handleOpenModal()}>
          <PlusIcon style={{width: '16px'}} /> New Task
        </button>
        
        <div className="d-flex gap-2 flex-grow-1 flex-md-grow-0">
          <div className="input-group input-group-sm rounded-pill overflow-hidden bg-light" style={{ border: '1px solid #dfe6e9', width: '200px' }}>
            <span className="input-group-text bg-transparent border-0"><MagnifyingGlassIcon style={{width: '16px', color: '#b2bec3'}} /></span>
            <input type="text" className="form-control bg-transparent border-0 shadow-none ps-0" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>

          <select className="form-select form-select-sm rounded-pill bg-light border-0 shadow-none" style={{ width: '140px', color: '#2d3436' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            {Object.keys(STATUS_COLORS).map(status => <option key={status} value={status}>{status}</option>)}
          </select>
        </div>
      </div>

      {/* TASK BOARD */}
      {loading ? (
        <div className="text-center py-5 text-muted small">Loading board...</div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-5 text-muted bg-white rounded-4 shadow-sm border-0 small">No tasks found.</div>
      ) : (
        Object.keys(groupedTasks).map((groupName, index) => {
          const groupColor = ['#74b9ff', '#00b894', '#ff7675', '#fdcb6e', '#a29bfe'][index % 5]; 
          
          return (
            <div key={groupName} className="mb-4">
              <h6 className="fw-bold mb-3 ms-2 d-flex align-items-center gap-2" style={{ color: groupColor, letterSpacing: '0.5px' }}>
                <span style={{ fontSize: '1rem' }}>▼</span> {groupName}
              </h6>
              
              <div className="bg-white rounded-4 shadow-sm overflow-hidden border-0">
                <div className="table-responsive">
                  <table className="table table-borderless table-hover align-middle mb-0" style={{ minWidth: '700px', fontSize: '0.9rem' }}>
                    <thead className="bg-light text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      <tr>
                        <th className="ps-4" style={{ width: '35%' }}>Task</th>
                        <th className="text-center" style={{ width: '60px' }}>User</th>
                        <th className="text-center" style={{ width: '160px' }}>Status</th>
                        <th className="text-center" style={{ width: '120px' }}>Progress</th>
                        <th className="text-center" style={{ width: '120px' }}>Deadline</th>
                        <th className="text-center" style={{ width: '90px' }}>Act</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedTasks[groupName].map((task) => {
                        const style = STATUS_COLORS[task.status] || STATUS_COLORS['Not Started Yet'];
                        const progress = getProgressValue(task.status);

                        return (
                          <tr key={task.id} style={{ borderBottom: '1px solid #f1f2f6' }}>
                            
                            {/* TASK TITLE */}
                            <td className="ps-0 py-2">
                              <div className="d-flex align-items-center h-100">
                                <div style={{ width: '4px', height: '30px', backgroundColor: groupColor, marginRight: '15px', borderRadius: '0 4px 4px 0' }}></div>
                                <span className="text-dark fw-medium text-truncate" style={{ cursor: 'pointer' }} onClick={() => handleOpenViewModal(task)}>
                                  {task.title}
                                </span>
                              </div>
                            </td>

                            {/* USER AVATAR */}
                            <td className="text-center py-2">
                                <div className="d-inline-flex justify-content-center align-items-center text-white rounded-circle shadow-sm" 
                                     style={{ width: '30px', height: '30px', backgroundColor: '#636e72', fontSize: '12px' }}
                                     title={task.assigneeName}>
                                  {task.assigneeName ? task.assigneeName.charAt(0).toUpperCase() : '?'}
                                </div>
                            </td>

                            {/* STATUS (NATIVE SELECT) */}
                            <td className="p-1 text-center align-middle">
                              <select 
                                value={task.status} 
                                onChange={(e) => handleStatusChange(task.id, e.target.value)}
                                className="form-select form-select-sm border-0 shadow-none text-center fw-bold w-100"
                                style={{ 
                                  backgroundColor: style.bg, 
                                  color: style.text, 
                                  cursor: 'pointer',
                                  appearance: 'none',
                                  height: '32px',
                                  borderRadius: '0'
                                }}
                              >
                                {Object.keys(STATUS_COLORS).map(statusKey => (
                                  <option key={statusKey} value={statusKey} style={{backgroundColor: '#fff', color: '#2d3436'}}>
                                    {statusKey}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* PERCENTAGE PROGRESS */}
                            <td className="text-center py-2">
                              <div className="d-flex align-items-center justify-content-center gap-2 w-100 px-2">
                                <div className="progress flex-grow-1" style={{ height: '8px', backgroundColor: '#dfe6e9', borderRadius: '10px' }}>
                                  <div className="progress-bar" role="progressbar" style={{ width: `${progress}%`, backgroundColor: style.bg, borderRadius: '10px' }}></div>
                                </div>
                                <span className="small fw-bold" style={{ color: style.bg, minWidth: '35px' }}>{progress}%</span>
                              </div>
                            </td>

                            {/* DEADLINE */}
                            <td className="text-center py-2 text-muted small">
                              {formatDateTime(task.dueDate)}
                            </td>

                            {/* EDIT & VIEW ICONS */}
                            <td className="text-center py-2">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Görüntüleme Butonu (Yeni) */}
                                <button className="btn btn-sm p-1 text-primary hover-primary" onClick={() => handleOpenViewModal(task)} title="View Details">
                                  <EyeIcon style={{width: '18px'}} />
                                </button>

                                {/* Düzenleme Butonu */}
                                <button className="btn btn-sm p-1 text-muted hover-primary" onClick={() => handleOpenModal(task)} title="Edit Task">
                                  <PencilSquareIcon style={{width: '18px'}} />
                                </button>
                                
                                {/* Not Göstergesi */}
                                {task.notes && <ChatBubbleOvalLeftEllipsisIcon className="text-info mt-1" style={{width: '16px'}} title="Has Notes" />}
                              </div>
                            </td>

                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* SAYFALAMA */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <ul className="pagination pagination-sm shadow-sm rounded-pill overflow-hidden">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link border-0 text-dark" onClick={() => setCurrentPage(prev => prev - 1)}>Prev</button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link border-0" onClick={() => setCurrentPage(i + 1)} style={currentPage === i + 1 ? {backgroundColor: '#2d3436', color: 'white'} : {color: '#636e72'}}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link border-0 text-dark" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
            </li>
          </ul>
        </div>
      )}

      {/* MODALLAR */}
      <TaskModal show={showModal} handleClose={handleCloseModal} task={selectedTask} users={teamUsers} onSave={handleSaveTask} onDelete={handleDeleteTask} />
      
      {/* YENİ EKLENEN GÖRÜNTÜLEME MODALI */}
      <TaskViewModal show={showViewModal} handleClose={handleCloseViewModal} task={viewingTask} />

    </div>
  );
};

export default TaskBoard;